import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiUser,
  FiUsers
} from "react-icons/fi";
import { NotificationManager } from "react-notifications";
import { Link, useNavigate } from 'react-router-dom';
import ActionsMenu from '../../../components/ActionsMenu';
import DeleteConfirmationModal from '../../../components/ConfirmationModals/DeleteConfirmationModal';
import Loader from '../../../components/Loader';
import ContactPagination from '../../../components/Pagination/ContactPagination';
import useAuth from '../../../hooks/useAuth';
import { BASE_URL } from '../../../utils/Element';
import EditLeadModal from '../EditLeadModal';
import AssignLeadModal from './AssignLeadModal';
import ConvertPipelineModal from './ConvertPipelineModal';

const LeadsDashboard = ({
  dashboardData,
  onPageChange,
  onPageSizeChange,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [selectedLeadType, setSelectedLeadType] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [config] = useAuth();

  // Helper functions
  const hasValue = (value) => {
    if (value == null) return false;
    const str = getString(value);
    return str.trim().length > 0;
  };

  const getString = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'object') {
      return value.primary ||
        value.name ||
        value.formatted ||
        value.value ||
        value.label ||
        Object.values(value).find(v => typeof v === 'string') ||
        JSON.stringify(value).replace(/[\{\}""]/g, '');
    }
    return String(value).trim();
  };

  // Initialize expanded sections
  useEffect(() => {
    if (dashboardData?.data?.sections) {
      const initialExpanded = {};
      dashboardData.data.sections.forEach((section) => {
        initialExpanded[section.key] = section.is_expanded ?? true;
      });
      setExpandedSections(initialExpanded);
    }
  }, [dashboardData]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const refreshDashboard = () => {
    onRefresh?.();
  };

  const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/markAs-Proposal/${selectedId}`, { mark_as_proposal: true }, config)
      .then((res) => {
        setShowConfirmModal(false);
        if (res?.data?.message) NotificationManager.success(res.data.message);
        refreshDashboard();
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message);
        }
      });
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-lead/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        setShowDeleteModal(false);
        if (res?.data?.message) NotificationManager.success(res.data.message);
        refreshDashboard();
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const leadTypeValue = selectedLeadType === 0 ? "Disposition" : "Acquisition";

  if (!dashboardData?.success || !dashboardData?.data) {
    return <Loader />;
  }

  // ✅ Correct order: Destructure first, then get nextUp
  const { round_robin, overview, sections = [] } = dashboardData.data;
  const nextUp = round_robin?.next_up;

  return (
    <div className="space-y-3 mb-6">
      {/* Round Robin */}
      <div className="bg-white border rounded-2xl p-3">
        <div className="flex items-center justify-between mb-4">
          <h2 className="head-5 dark-H">ROUND ROBIN ORDER</h2>
          <div className="head-5">Next Up</div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {round_robin?.order?.map((advisor, idx) => (
            <div
              key={advisor.user_id || idx}
              className={`flex-1 min-w-[140px] p-4 rounded-xl border ${advisor.is_next_up ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center body-N font-medium">
                  {advisor.position}
                </div>
                <div>
                  <div className="body-N">{getString(advisor.name)}</div>
                  {advisor.is_next_up && <div className="head-5 green-H">NEXT UP</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
        {[
          { icon: FiDatabase, count: overview?.unassigned_bds_leads, label: "Unassigned BDS Leads", color: "text-green-600" },
          { icon: FiUsers, count: overview?.advisor_generated_leads, label: "Advisor-Generated Leads", color: "text-blue-600" },
          { icon: FiClock, count: overview?.assigned_bds_leads_0_90_days, label: "Assigned Leads (0-90)", color: "text-orange-600" },
          { icon: FiAlertTriangle, count: overview?.older_leads_90_plus_days, label: "Older Leads (>90)", color: "text-purple-600" },
          { icon: FiCheckCircle, count: overview?.in_pipeline_converted, label: "In Pipeline (Converted)", color: "text-emerald-600" },
        ].map((item, i) => (
          <div key={i} className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <item.icon className={`w-6 h-6 ${item.color}`} />
              <div className="head-2 dark-H">{item.count ?? 0}</div>
            </div>
            <div className="mt-4 body-N">{item.label}</div>
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {sections.map((section) => {
          const sectionKey = section.key;
          const pagination = section.pagination || {};
          const isUnassignedBDS = sectionKey === 'unassigned_bds_leads';

          return (
            <div key={sectionKey} className="bg-white border rounded-2xl overflow-hidden">
              {/* Section Header */}
              <div
                className={`px-4 py-4 flex items-center justify-between border-b cursor-pointer ${section.accent === 'green' ? 'bg-green-50 border-green-200' :
                  section.accent === 'blue' ? 'bg-blue-50 border-blue-200' :
                    section.accent === 'orange' ? 'bg-orange-50 border-orange-200' :
                      'bg-purple-50 border-purple-200'
                  }`}
                onClick={() => toggleSection(sectionKey)}
              >
                <div>
                  <div className="head-5 green-H">{getString(section.title)}</div>
                  <div className="body-S text-gray-500 mt-0.5">{getString(section.description)}</div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-emerald-600 hover:underline cursor-pointer text-sm font-medium">
                    {getString(section.view_all_label)}
                  </div>
                  {expandedSections[sectionKey] ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              {expandedSections[sectionKey] && (
                <>
                  <div className="h-[420px] overflow-auto border-t">
                    <table className="w-full min-w-[500px] border-collapse">
                      <thead className="sticky top-0 z-20 bg-[#d1dbcf]">
                        <tr className="text-left uppercase body-N dark-M">
                          <th className="px-8 py-4 w-28">LEAD DATE</th>
                          <th className="px-4 py-4 w-56">Contact Name</th>
                          <th className="px-4 py-4 w-80">Property / Acquisition Criteria</th>
                          <th className="px-4 py-4 w-44">LEAD SOURCE</th>
                          <th className="px-4 py-4 w-40">
                            {isUnassignedBDS ? 'BDS' : 'LEAD OWNER'}
                          </th>
                          {!isUnassignedBDS && (
                            <th className="px-4 py-4 w-40">BDS</th>
                          )}
                          <th className="px-4 py-4 w-40">LAST CONTACTED</th>
                          <th className="px-8 py-4 text-right w-28">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody className="body-N dark-M">
                        {section.items?.length > 0 ? (
                          section.items.map((lead) => (
                            <tr key={lead.id || Math.random()} className="border-t hover:bg-gray-50">
                              {/* ... rest of your table rows (unchanged) ... */}
                              <td className="px-8 py-5 font-medium text-gray-700 whitespace-nowrap">
                                {getString(lead.lead_date_formatted || lead.lead_date)}
                              </td>

                              <td className="px-4 py-5 text-wrap">
                                <div className="font-medium flex items-center gap-2">
                                  {hasValue(lead.contact) && (
                                    <div
                                      className="flex items-center gap-2 cursor-pointer"
                                      onClick={() => navigate(`/contact/${lead.contact_id || lead.contact?.id}`)}
                                    >
                                      <FiUser className="text-gray-400 flex-shrink-0" />
                                      {getString(lead.contact)}
                                    </div>
                                  )}
                                </div>
                                {(hasValue(lead.contact?.secondary) || hasValue(lead.company)) && (
                                  <div className="text-xs text-gray-500 mt-0.5 pl-6">
                                    <span
                                      className="cursor-pointer"
                                      onClick={() => navigate(`/company/${lead.company_id || lead.company?.id}`)}
                                    >
                                      {getString(lead.contact?.secondary || lead.company)}
                                    </span>
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-5 text-gray-700 text-wrap">
                                <Link
                                  to={`/property/${lead.property_or_acquisition?.id}`}
                                  className="dark-H head-5"
                                >
                                  {lead.property_or_acquisition?.primary}
                                </Link>

                                <div>{lead.property_or_acquisition?.secondary?.street}</div>

                                <div>
                                  {[
                                    lead.property_or_acquisition?.secondary?.city,
                                    lead.property_or_acquisition?.secondary?.state,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </div>
                              </td>

                              <td className="px-4 py-5 text-gray-600 text-wrap">
                                {getString(lead.lead_source)}
                              </td>

                              <td className="px-4 py-5 text-wrap">
                                {hasValue(isUnassignedBDS ? lead.bds : lead.lead_owner) && (
                                  <Link
                                    to={`/user/${lead.bds?.id || lead.lead_owner?.id}`}
                                    onClick={(e) => { e.preventDefault(); navigate(`/user/${lead.bds?.id || lead.lead_owner?.id}`); }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <FiUser className="flex-shrink-0" />
                                      {getString(isUnassignedBDS ? lead.bds : lead.lead_owner)}
                                    </div>
                                  </Link>
                                )}
                              </td>

                              {!isUnassignedBDS && (
                                <td className="px-4 py-5 text-wrap">
                                  {hasValue(lead.bds) && (
                                    <Link
                                      to={`/user/${lead.bds?.id}`}
                                      onClick={(e) => { e.preventDefault(); navigate(`/user/${lead.bds?.id}`); }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <FiUser className="flex-shrink-0" />
                                        {getString(lead.bds)}
                                      </div>
                                    </Link>
                                  )}
                                </td>
                              )}

                              <td className="px-4 py-5 text-gray-600 text-sm whitespace-nowrap">
                                {(lead.last_contacted)
                                  ? moment(lead.last_contacted).format("MM/DD/YY")
                                  : ''}
                                <span className="dark-M body-S">
                                  {(lead.last_contacted)
                                    ? ` (${moment(lead.last_contacted).fromNow()})`
                                    : ''}
                                </span>
                              </td>

                              <td className="px-8 py-5 flex justify-center">
                                  <ActionsMenu
                                    from={isUnassignedBDS ? "unassigned-lead" : "lead"}
                                    handleEdit={() => { setShowEditModal(true); setSelectedId(lead.id); }}
                                    handleDelete={() => { setSelectedId(lead.id); setShowDeleteModal(true); }}
                                    handleAssign={isUnassignedBDS ? () => {
                                      setSelectedId(lead.id);
                                      setShowAssignModal(true);
                                    } : undefined}
                                    showOtherOption={!isUnassignedBDS && lead.lead_type === 0}
                                    otherOptionTitle="Convert to Pipeline"
                                    handleOtherOption={() => {
                                      setShowConfirmModal(true);
                                      setSelectedId(lead.id);
                                      setSelectedLeadType(lead.lead_type);
                                    }}
                                  />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={isUnassignedBDS ? 7 : 8}
                              className="px-8 py-16 text-center text-gray-500"
                            >
                              No leads found in this section
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {pagination.last_page > 1 && (
                    <ContactPagination
                      paginationData={{
                        from: pagination.from,
                        to: pagination.to,
                        totalItems: pagination.total,
                      }}
                      itemPerPage={pagination.per_page || 100}
                      handlePrev={() => onPageChange(sectionKey, pagination.current_page - 1)}
                      handleNext={() => onPageChange(sectionKey, pagination.current_page + 1)}
                      handleItemPerPageSelect={(value) => onPageSizeChange(sectionKey, value)}
                      onSuccess={() => { }}
                      from="dashboard"
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Modals */}
      <EditLeadModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={refreshDashboard}
        from="Lead"
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      <AssignLeadModal
        showModal={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        id={selectedId}
        onSuccess={refreshDashboard}
        nextUp={nextUp}          
      />

      <ConvertPipelineModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        from="lead"
        id={selectedId}
        leadType={leadTypeValue}
      />
    </div>
  );
};

export default LeadsDashboard;